import { useEffect, useState } from "react";
import { mdiClose, mdiMagnify } from "@mdi/js";
import {
  AutocompleteRenderInputParams,
  Box,
  IconButton,
  InputAdornment,
  MenuItemProps,
  TextField,
  useTheme,
} from "@mui/material";
import {
  AutocompleteOption,
  MiAutocomplete,
} from "../MiAutocomplete/MiAutocomplete";
import { MiIcon } from "../MiIcon/MiIcon";
import { MiLoader } from "../MiLoader/MiLoader";

export function MiSearchAutocomplete<OptionDataType extends { id: string }>({
  search,
  onSearchChange,
  onClearSearch,
  width,
  iconColor,
  renderOption,
  clearOnBlur = false,
  onEnterPress,
  options,
  getOptions,
  debouncedSearch,
  loading,
  disabled,
  renderInput,
  label,
  placeholder,
  getOptionLabel: getOptionLabelFn = (option: any) => option.label || "",
  noOptionsText = "No options",
}: Readonly<MiSearchAutocompleteProps<OptionDataType>>) {
  const theme = useTheme();
  const [inputTouched, setInputTouched] = useState(false);

  const handleOnChange = (
    _: React.SyntheticEvent,
    value: OptionDataType | null
  ) => {
    if (value && value.id) {
      onClearSearch?.();
    }
  };

  const handleInputChange = (event: React.SyntheticEvent, value: string) => {
    if (event) {
      onSearchChange?.(value.trimStart());
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      onEnterPress?.();
    }
  };

  const handleRenderInput = (params: AutocompleteRenderInputParams) => {
    if (renderInput) {
      return renderInput(params);
    }

    return (
      <TextField
        {...params}
        label={label}
        placeholder={placeholder}
        fullWidth
        size="small"
        disabled={disabled}
        slotProps={{
          input: {
            ...params.InputProps,
            style: {
              paddingRight: theme.spacing(1.5),
            },
            inputProps: {
              "data-testid": `mi-search-autocomplete-input`,
              ...params.inputProps,
            },
            startAdornment: (
              <InputAdornment position="start">
                <MiIcon path={mdiMagnify} size={0.8} color={iconColor} />
              </InputAdornment>
            ),
            endAdornment: search && (
              <InputAdornment position="end">
                {loading ? <MiLoader isLoading={loading} size={20} /> : null}
                <IconButton
                  size="small"
                  onClick={onClearSearch}
                  data-testid="mi-search-autocomplete-clear-button"
                >
                  <MiIcon path={mdiClose} size={0.8} color={iconColor} />
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />
    );
  };

  useEffect(() => {
    if (inputTouched) {
      getOptions?.();
    }
  }, [inputTouched, debouncedSearch]);

  return (
    <Box width={width}>
      <MiAutocomplete
        options={options ?? []}
        openOnFocus
        onOpen={() => setInputTouched(true)}
        clearOnBlur={clearOnBlur}
        inputValue={search}
        onInputChange={handleInputChange}
        onChange={handleOnChange}
        getOptionLabel={getOptionLabelFn}
        onKeyDownCapture={handleKeyPress}
        renderOption={renderOption}
        renderInput={handleRenderInput}
        blurOnSelect
        noOptionsText={noOptionsText}
      />
    </Box>
  );
}

interface MiSearchAutocompleteProps<OptionDataType = any> {
  label?: string;
  placeholder?: string;
  search: string;
  debouncedSearch?: string;
  onSearchChange: (value: string) => void;
  onClearSearch?: () => void;
  width?: string;
  onNavigate?: (option: OptionDataType) => void;
  iconColor?: string;
  clearOnBlur?: boolean;
  onEnterPress?: () => void;
  renderOption?: (
    props: MenuItemProps,
    option: OptionDataType,
    close: () => void
  ) => React.ReactNode;
  options: AutocompleteOption[];
  getOptions?: () => Promise<void> | Promise<OptionDataType[]>;
  loading?: boolean;
  disabled?: boolean;
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode;
  getOptionLabel?: (option: OptionDataType) => string;
  noOptionsText?: string;
}
